import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { LuCalendar, LuDatabase, LuPencil, LuTrash2 } from "react-icons/lu";

import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { ItemSortBy, ItemSortOrder, ItemsProps } from "../../types/item";
import { TagIcon } from "../icons";
import Button from "../ui/Button";
import PhotoCell from "../ui/PhotoCell";
import SortableTableHead from "../ui/SortableTableHead";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const editPath = (id: number) => route.itemEdit.replace(":id", String(id));

type Props = {
  items: ItemsProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  // The order the items were loaded in.
  sortBy: ItemSortBy;
  sortOrder: ItemSortOrder;
  onSort: (sortBy: ItemSortBy, sortOrder: ItemSortOrder) => void;
  // The names of the list's sorts, for the headers' hover text.
  sortLabels: Record<ItemSortBy, string>;
  handleDeleteItem: (id: number) => void;
};

const ItemList = ({
  items,
  pageNumber,
  pageSize,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  sortLabels,
  handleDeleteItem,
}: Props) => {
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.itemsUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.itemsDelete
  );

  const actionCount = (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 6 + (actionCount > 0 ? 1 : 0);

  const handleEditItem = (item: ItemsProps) => {
    navigate(editPath(item.id));
  };

  const handleDeleteRow = async (item: ItemsProps) => {
    // Sub items are deleted along with their item.
    const confirmed = await confirmDelete(
      item.sub_items_count > 0
        ? `તેની ${item.sub_items_count} સબ આઈટમ પણ કાઢી નખાશે, તમે આને પાછું ફેરવી શકશો નહીં!`
        : `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      handleDeleteItem(item.id);
    }
  };

  // The date header shows both dates, so it also stands for the last-changed date the filter can pick.
  const sortHead = (column: ItemSortBy, label: string, className?: string) => (
    <SortableTableHead
      column={column}
      alsoFor={column === "created_at" ? ["updated_at"] : undefined}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      sortLabels={sortLabels}
      disabled={isLoading}
      className={className}
    >
      {label}
    </SortableTableHead>
  );

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {sortHead("id", "ક્રમ")}
                  <TableHead className={tableHeadCellClass}>ફોટો</TableHead>
                  {sortHead("name", "આઈટમ નામ")}
                  {sortHead("category", "કેટેગરી")}
                  {sortHead("sub_items_count", "S. Count", "text-center")}
                  {sortHead("created_at", "તારીખ")}
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : items.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="આઈટમ ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        <PhotoCell url={item.image_url} alt={item.name} />
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TagIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div>
                            <div className="font-medium">{item.name}</div>
                            <div className="text-sm text-gray-500">
                              ID: {item.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>{item.category?.name || "-"}</TableCell>

                      <TableCell className="text-center">
                        {item.sub_items_count}
                      </TableCell>

                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(item.created_at)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDate(item.updated_at)}
                          </span>
                        </div>
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="iconSm"
                                title="સંપાદિત કરો"
                                onClick={() => handleEditItem(item)}
                              >
                                <LuPencil className="h-4 w-4" />
                              </Button>
                            )}

                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="iconSm"
                                title="કાઢી નાખો"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteRow(item)}
                              >
                                <LuTrash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : items.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="આઈટમ ની વિગતો મળી નથી" />
          </div>
        ) : (
          items.map((item, index) => (
            <div key={item.id} className={mobileCardClass}>
              <div className="flex items-center gap-2 border-b p-4">
                <TagIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <div className="font-medium break-words">{item.name}</div>
                  <div className="text-sm text-gray-500">ID: {item.id}</div>
                </div>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ફોટો</span>
                  <PhotoCell url={item.image_url} alt={item.name} />
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>કેટેગરી</span>
                  <span className="text-right">{item.category?.name || "-"}</span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>S. Count</span>
                  <span className="text-right">{item.sub_items_count}</span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(item.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDate(item.updated_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                  )}
                >
                  {canUpdate && (
                    <Button
                      variant="outline"
                      title="સંપાદિત કરો"
                      onClick={() => handleEditItem(item)}
                    >
                      <LuPencil className="h-4 w-4" />
                    </Button>
                  )}

                  {canDelete && (
                    <Button
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      title="કાઢી નાખો"
                      onClick={() => handleDeleteRow(item)}
                    >
                      <LuTrash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default ItemList;
